import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, Pencil, Trash2, LogOut, MousePointerClick, Mail, MessageSquare, Table2, BadgeCheck, Handshake, LayoutDashboard, ShieldAlert, Wand2, Link2, ClipboardList, Radio, Users, Trophy } from "lucide-react";
import { AdminAffiliateOps } from "@/components/AdminAffiliateOps";
import { AdminOverview } from "@/components/AdminOverview";
import { AdminFraudAlerts } from "@/components/AdminFraudAlerts";
import { AdminLinkManager } from "@/components/AdminLinkManager";
import { AdminOfficialLinks } from "@/components/AdminOfficialLinks";
import { AdminManualSprint } from "@/components/AdminManualSprint";
import { AdminBroadcasts } from "@/components/AdminBroadcasts";
import { AdminUsers } from "@/components/AdminUsers";
import { AdminSignupProofs } from "@/components/AdminSignupProofs";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDIT_FIELDS = [
  ["commission", "Commission (display)", "text"],
  ["commission_value", "Commission value (numeric)", "number"],
  ["cookie_days", "Cookie days", "number"],
  ["payout_threshold", "Payout threshold ($)", "number"],
  ["payout_frequency", "Payout frequency", "text"],
  ["epc", "EPC ($)", "number"],
  ["aov", "AOV ($)", "number"],
  ["conversion_rate", "Conversion rate (%)", "number"],
  ["popularity", "Popularity (0-100)", "number"],
  ["join_url", "Join URL", "text"],
];

const FLAGS = [
  ["beginner_friendly", "Beginner friendly"],
  ["editors_choice", "Editor's choice"],
  ["high_ticket", "High ticket"],
  ["recurring", "Recurring"],
  ["is_exclusive", "Members Club exclusive ($1.99/mo)"],
];

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [checking, setChecking] = useState(false);

  const [programs, setPrograms] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const client = useCallback(() => axios.create({ headers: { "X-Admin-Key": adminKey } }), [adminKey]);

  const loadAll = useCallback(() => {
    const c = client();
    axios.get(`${API}/programs`, { params: { limit: 60, sort: "popularity" } }).then((r) => setPrograms(r.data.programs));
    c.get(`${API}/track/join-clicks`).then((r) => setClicks(r.data.stats));
    c.get(`${API}/admin/subscribers`).then((r) => setSubscribers(r.data.subscribers));
    c.get(`${API}/admin/contacts`).then((r) => setContacts(r.data.contacts));
    c.get(`${API}/admin/featured-submissions`).then((r) => setFeatured(r.data.submissions));
  }, [client]);

  useEffect(() => {
    if (!adminKey) return;
    setChecking(true);
    client().get(`${API}/admin/verify`)
      .then(() => { setAuthed(true); sessionStorage.setItem("admin_key", adminKey); })
      .catch(() => { setAuthed(false); sessionStorage.removeItem("admin_key"); toast.error("Invalid admin key"); })
      .finally(() => setChecking(false));
  }, [adminKey, client]);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);

  const saveProgram = async () => {
    setSaving(true);
    try {
      const payload = {};
      EDIT_FIELDS.forEach(([k, , type]) => {
        const v = editing[k];
        payload[k] = type === "number" ? parseFloat(v) || 0 : v;
      });
      FLAGS.forEach(([k]) => { payload[k] = !!editing[k]; });
      payload.description = editing.description;
      await client().put(`${API}/admin/programs/${editing.slug}`, payload);
      toast.success(`${editing.name} updated`);
      setEditing(null);
      loadAll();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteProgram = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    try {
      await client().delete(`${API}/admin/programs/${p.slug}`);
      toast.success(`${p.name} deleted`);
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!authed) {
    return (
      <main className="max-w-md mx-auto px-4 py-24">
        <div className="bg-white border border-slate-200 rounded-lg p-10">
          <KeyRound className="w-8 h-8 text-emerald-600 mb-4" />
          <h1 className="font-heading text-2xl tracking-tight mb-2">Admin access</h1>
          <p className="text-sm text-slate-500 mb-6">Enter your admin key to manage program data.</p>
          <form onSubmit={(e) => { e.preventDefault(); setAdminKey(keyInput.trim()); }} className="space-y-4">
            <input
              type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key" autoComplete="off" data-testid="admin-key-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button type="submit" disabled={checking || !keyInput.trim()} data-testid="admin-key-submit"
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors duration-200 disabled:opacity-60">
              {checking && <Loader2 className="w-4 h-4 animate-spin" />} Enter admin panel
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-panel" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">Admin Panel</p>
          <h1 className="font-heading text-4xl tracking-tight">Data management</h1>
        </div>
        <button onClick={() => { setAuthed(false); setAdminKey(""); setKeyInput(""); sessionStorage.removeItem("admin_key"); }} data-testid="admin-logout-button"
          className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors duration-200">
          <LogOut className="w-4 h-4" /> Lock panel
        </button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-full h-auto p-1.5 mb-8 flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-full px-5 py-2 gap-2"><LayoutDashboard className="w-4 h-4" /> Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full px-5 py-2 gap-2"><Users className="w-4 h-4" /> Users</TabsTrigger>
          <TabsTrigger value="signup-proofs" data-testid="admin-tab-signup-proofs" className="rounded-full px-5 py-2 gap-2"><Trophy className="w-4 h-4" /> Signup Proofs</TabsTrigger>
          <TabsTrigger value="programs" data-testid="admin-tab-programs" className="rounded-full px-5 py-2 gap-2"><Table2 className="w-4 h-4" /> Programs ({programs.length})</TabsTrigger>
          <TabsTrigger value="clicks" data-testid="admin-tab-clicks" className="rounded-full px-5 py-2 gap-2"><MousePointerClick className="w-4 h-4" /> Join Clicks</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers" className="rounded-full px-5 py-2 gap-2"><Mail className="w-4 h-4" /> Subscribers ({subscribers.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-full px-5 py-2 gap-2"><MessageSquare className="w-4 h-4" /> Messages ({contacts.length})</TabsTrigger>
          <TabsTrigger value="featured" data-testid="admin-tab-featured" className="rounded-full px-5 py-2 gap-2"><BadgeCheck className="w-4 h-4" /> Featured ({featured.length})</TabsTrigger>
          <TabsTrigger value="affiliate-ops" data-testid="admin-tab-affiliate-ops" className="rounded-full px-5 py-2 gap-2"><Handshake className="w-4 h-4" /> Ambassador Ops</TabsTrigger>
          <TabsTrigger value="fraud" data-testid="admin-tab-fraud" className="rounded-full px-5 py-2 gap-2"><ShieldAlert className="w-4 h-4" /> Fraud Alerts</TabsTrigger>
          <TabsTrigger value="link-manager" data-testid="admin-tab-link-manager" className="rounded-full px-5 py-2 gap-2"><Wand2 className="w-4 h-4" /> Link Manager</TabsTrigger>
          <TabsTrigger value="official-links" data-testid="admin-tab-official-links" className="rounded-full px-5 py-2 gap-2"><Link2 className="w-4 h-4" /> Official Links</TabsTrigger>
          <TabsTrigger value="manual-sprint" data-testid="admin-tab-manual-sprint" className="rounded-full px-5 py-2 gap-2"><ClipboardList className="w-4 h-4" /> Manual Sprint</TabsTrigger>
          <TabsTrigger value="broadcasts" data-testid="admin-tab-broadcasts" className="rounded-full px-5 py-2 gap-2"><Radio className="w-4 h-4" /> Broadcasts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <AdminOverview client={client} API={API} />
        </TabsContent>

        <TabsContent value="users">
          <AdminUsers adminKey={adminKey} />
        </TabsContent>

        <TabsContent value="signup-proofs">
          <AdminSignupProofs adminKey={adminKey} />
        </TabsContent>

        <TabsContent value="programs">
          <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="p-4">Program</th><th className="p-4">Niche</th><th className="p-4">Commission</th><th className="p-4">Cookie</th><th className="p-4">EPC</th><th className="p-4">Threshold</th><th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((p) => (
                  <tr key={p.slug} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                    <td className="p-4 font-medium">{p.name}</td>
                    <td className="p-4 text-slate-500">{p.niche_name}</td>
                    <td className="p-4">{p.commission}</td>
                    <td className="p-4">{p.cookie_days}d</td>
                    <td className="p-4">${p.epc.toFixed(2)}</td>
                    <td className="p-4">${p.payout_threshold}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditing({ ...p })} data-testid={`admin-edit-${p.slug}`} aria-label={`Edit ${p.name}`}
                          className="p-2 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteProgram(p)} data-testid={`admin-delete-${p.slug}`} aria-label={`Delete ${p.name}`}
                          className="p-2 rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="clicks">
          <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl">
            <p className="text-sm text-slate-500 mb-4">Most-clicked "Join Program" buttons across the site.</p>
            {clicks.length === 0 ? (
              <p data-testid="admin-clicks-empty" className="text-sm text-slate-400 py-6">No join clicks tracked yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {clicks.map((c, i) => (
                  <li key={c.slug} className="flex items-center justify-between py-3">
                    <span className="text-sm font-medium"><span className="text-slate-400 mr-3">{i + 1}.</span>{c.program_name}</span>
                    <span className="text-sm font-semibold text-emerald-700">{c.clicks} clicks</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="subscribers">
          <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl">
            {subscribers.length === 0 ? <p className="text-sm text-slate-400 py-6">No subscribers yet.</p> : (
              <ul className="divide-y divide-slate-100">
                {subscribers.map((s) => (
                  <li key={s.id} className="flex items-center justify-between py-3 text-sm">
                    <span className="font-medium">{s.email}</span>
                    <span className="text-slate-400 text-xs">{new Date(s.subscribed_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="contacts">
          <div className="space-y-4 max-w-2xl">
            {contacts.length === 0 ? <p className="text-sm text-slate-400 py-6">No messages yet.</p> : contacts.map((m) => (
              <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">{m.name} <span className="text-slate-400 font-normal">· {m.email}</span></p>
                  <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{m.message}</p>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="featured">
          <div className="space-y-4 max-w-3xl">
            {featured.length === 0 ? <p data-testid="admin-featured-empty" className="text-sm text-slate-400 py-6">No featured listing purchases yet.</p> : featured.map((f) => (
              <div key={f.id} data-testid={`admin-featured-${f.id}`} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">{f.program_name} {f.company && <span className="text-slate-400 font-normal">· {f.company}</span>}</p>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${f.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{f.payment_status}</span>
                </div>
                <p className="text-xs text-slate-500">{f.email} · <a href={f.website} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{f.website}</a></p>
                <p className="text-xs text-slate-400 mt-1">{new Date(f.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="affiliate-ops">
          <AdminAffiliateOps client={client} API={API} />
        </TabsContent>

        <TabsContent value="fraud">
          <AdminFraudAlerts client={client} API={API} />
        </TabsContent>

        <TabsContent value="link-manager">
          <AdminLinkManager client={client} API={API} />
        </TabsContent>

        <TabsContent value="official-links">
          <AdminOfficialLinks client={client} API={API} />
        </TabsContent>

        <TabsContent value="manual-sprint">
          <AdminManualSprint client={client} API={API} />
        </TabsContent>

        <TabsContent value="broadcasts">
          <AdminBroadcasts client={client} API={API} />
        </TabsContent>

      </Tabs>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-heading">Edit {editing.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">Description</label>
                  <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    data-testid="admin-edit-description"
                    className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {EDIT_FIELDS.map(([key, label, type]) => (
                    <div key={key}>
                      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">{label}</label>
                      <input type={type} value={editing[key] ?? ""} step="any"
                        onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                        data-testid={`admin-edit-${key}`}
                        className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  {FLAGS.map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5">
                      <span className="text-sm font-medium">{label}</span>
                      <Switch checked={!!editing[key]} onCheckedChange={(v) => setEditing({ ...editing, [key]: v })} data-testid={`admin-flag-${key}`} />
                    </div>
                  ))}
                </div>
                <button onClick={saveProgram} disabled={saving} data-testid="admin-save-program"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-60">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
